import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const refreshUsage = useCallback(async () => {
    if (token) {
      try {
        const response = await authAPI.getUsage();
        setUsage(response.data);
      } catch (error) {
        console.error('Failed to fetch usage:', error);
      }
    }
  }, [token]);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        const usageResponse = await authAPI.getUsage();
        setUsage(usageResponse.data);
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      authAPI.getMe()
        .then(response => {
          setUser(response.data);
          setLoading(false);
          // Also fetch usage
          return authAPI.getUsage();
        })
        .then(response => {
          setUsage(response.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setToken(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Fetch usage after login
    const usageResponse = await authAPI.getUsage();
    setUsage(usageResponse.data);
    return userData;
  };

  const loginWithToken = async (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    // Fetch usage
    try {
      const usageResponse = await authAPI.getUsage();
      setUsage(usageResponse.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const signup = async (data) => {
    const response = await authAPI.signup(data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Fetch usage after signup
    const usageResponse = await authAPI.getUsage();
    setUsage(usageResponse.data);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setUsage(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      usage,
      loading, 
      token,
      login,
      loginWithToken,
      signup, 
      logout, 
      refreshUsage,
      refreshUser,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};